import * as preact from "preact";

import * as editCardDialog from "editCardDialog";
import * as premiumUpsellModal from "premiumUpsellModal";
import * as languageMap from "shared/languageMap";
import * as subscriptionPlans from "shared/subscriptionPlans";
import stores from "stores/stores";
import UserWordStore from "stores/UserWordStore";
import AskAIView from "views/AskAIView";
import ExplanationView from "views/ExplanationView";
import PracticePlayTTSView from "views/PracticePlayTTSView";

interface Props {
  userWord: UserWordStore;
  context: string;
  monolingualMode: boolean;
}

interface State {
  reachedLimit: boolean;
  fetched: boolean;
  definition?: string;
  englishTranslation?: string;
}

class SmartDictionaryView extends preact.Component<Props, State> {
  disposeUserWordChange?: () => void;

  constructor(props: Props) {
    super(props);
    this.state = {
      reachedLimit: false,
      fetched: false,
    };

    this.disposeUserWordChange = this.props.userWord.onChange(
      (userWord) => userWord,
      this.onChange
    );
  }

  onChange = () => {
    this.forceUpdate();
  };

  componentWillUnmount() {
    this.disposeUserWordChange?.();
  }

  upgrade() {
    premiumUpsellModal.show({
      title: "explanationsLimit",
      heading:
        "Upgrade for more than " +
        subscriptionPlans.permissions["free"].definitions +
        " explanations per day",
      subheading:
        "Really understand what you're reading with unlimited AI-based context-aware explanations!",
      showTitle: false,
      smartDefinitions: true,
    });
  }

  toggleLanguage() {
    stores.user.setSmartDictionaryInFirstLanguage(
      this.props.userWord.state.value.language,
      !stores.user.isSmartDictionaryInFirstLanguage(
        this.props.userWord.state.value.language
      )
    );

    // Reset the fetched state
    this.setState({
      definition: "",
      fetched: false,
      reachedLimit: false,
    });
  }

  render() {
    const language = this.props.userWord.state.value.language;
    const query = this.props.userWord.state.value.word;
    const translation = this.props.userWord.state.value.translation;
    const alternativeTranslations =
      this.props.userWord.state.value.alternativeTranslations;
    const limit = subscriptionPlans.permissions["free"].definitions;
    const { reachedLimit } = this.state;
    const editLabel = `Edit ${this.props.userWord.isPhrase() ? "phrase" : "word"}`;

    const toggleLinkText =
      !this.state.fetched || this.state.reachedLimit
        ? undefined
        : `Switch explanations to ${
            stores.user.isSmartDictionaryInFirstLanguage(language)
              ? languageMap.map[language].english
              : languageMap.map[stores.user.firstLanguage.value].english
          }`;

    return (
      <div class="smartDictionaryContent">
        <h2 class="smartDictionaryHeader">
          <PracticePlayTTSView text={query} language={language} />
          <div>
            {query}
            {translation && !this.props.monolingualMode ? (
              <>
                {/* {" "}
              <span style={{ fontWeight: 300 }}>-</span>{" "} */}
                {alternativeTranslations.length === 0 ? (
                  <div class="smartDictionaryTranslation">{translation}</div>
                ) : (
                  [translation, ...alternativeTranslations].map(
                    (thisTranslation, index) => (
                      <div class="smartDictionaryTranslation">
                        {index !== 0 ? " / " : ""}
                        {thisTranslation}
                        <button
                          class="removeButton"
                          onClick={() => {
                            const oldTranslations = [
                              translation,
                              ...alternativeTranslations,
                            ];
                            const newTranslations = [
                              ...oldTranslations.slice(0, index),
                              ...oldTranslations.slice(index + 1),
                            ];
                            this.props.userWord.save(
                              {
                                translation: newTranslations[0],
                                alternativeTranslations:
                                  newTranslations.slice(1),
                              },
                              { patch: true }
                            );
                          }}
                        >
                          <i class="icon icon-remove-sign"></i>
                        </button>
                      </div>
                    )
                  )
                )}
              </>
            ) : null}
          </div>
          {this.state.fetched ? (
            <button
              type="button"
              class="editWordButton"
              title={editLabel}
              aria-label={editLabel}
              onClick={() => this.editWord()}
            >
              <i class="icon-pencil" aria-hidden="true"></i>
            </button>
          ) : null}
        </h2>
        {reachedLimit && (
          <p>
            You've reached your daily limit of {limit} context-aware
            explanations. Get unlimited explanations by{" "}
            <a
              href="#"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.upgrade();
              }}
            >
              upgrading to premium
            </a>
            .
          </p>
        )}
        <ExplanationView
          word={query}
          context={this.props.context}
          language={language}
          showInFirstLanguage={stores.user.isSmartDictionaryInFirstLanguage(
            language
          )}
          cacheOnly={false}
          existingTranslations={
            this.props.monolingualMode
              ? []
              : [translation, ...alternativeTranslations]
          }
          onExplanationFetched={() => {
            if (!this.state.fetched) {
              this.setState({ fetched: true });
            }
          }}
          onSelectTranslation={
            this.props.monolingualMode
              ? undefined
              : (selectedTranslation) =>
                  this.addTranslation(selectedTranslation)
          }
        />
        {toggleLinkText && (
          <p>
            <a
              class="toggleLink"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.toggleLanguage();
              }}
            >
              {toggleLinkText}
            </a>
          </p>
        )}
        {this.state.fetched &&
          !this.state.reachedLimit &&
          stores.user.hasPremiumAccess.value &&
          !stores.user.hasPremiumPlusAccess.value &&
          languageMap.isLowResource(language) && (
            <p class="lowResourceNotice">
              Tip: {languageMap.map[language].english} has limited AI training
              data, so context-aware translations and explanations may be less
              accurate. Premium Plus uses a more capable AI model for better
              results.{" "}
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  premiumUpsellModal.show({
                    title: "lowResourceUpgrade",
                    heading: "Upgrade to Premium Plus for better AI model",
                    showTitle: false,
                    plus: true,
                  });
                }}
              >
                Upgrade
              </a>
            </p>
          )}
        {this.state.fetched && stores.user.hasFeature("askAI") ? (
          <AskAIView
            userWord={this.props.userWord}
            context={this.props.context}
          />
        ) : null}
      </div>
    );
  }

  addTranslation(translation: string) {
    this.props.userWord.save(
      {
        alternativeTranslations: [
          ...this.props.userWord.state.value.alternativeTranslations,
          translation,
        ],
      },
      { patch: true }
    );
  }

  editWord() {
    editCardDialog.show(this.props.userWord, {
      title: "Edit Word / Phrase",
    });
  }
}

export default SmartDictionaryView;
